import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { Permission } from '@shared/types';
import { useAuthStore } from './authStore';
import { Button, Card, EmptyState, SkeletonList } from '../components/ui';

/**
 * 路由守卫。
 *
 * 这里只做**体验层**的拦截（该不该进入某个页面、按钮要不要显示），
 * 真正的安全边界在 Worker 的 `requirePermission` —— 前端永远只是提示。
 */

export function RequireAuth({ children }: { children: ReactNode }) {
  const status = useAuthStore((state) => state.status);
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <SkeletonList rows={3} />
      </div>
    );
  }

  if (status === 'anonymous') {
    const target = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?continue=${encodeURIComponent(target)}`} replace />;
  }

  return <>{children}</>;
}

/**
 * 「必须先改密」闸门。
 *
 * 初始口令写在环境变量里，属于共享秘密；在改掉之前，Worker 会对所有业务接口
 * 返回 403。这里提前把用户带到改密页，而不是让他一路点到 403 才知道原因。
 */
export function RequirePasswordChanged({ children }: { children: ReactNode }) {
  const session = useAuthStore((state) => state.session);
  const location = useLocation();

  if (session?.mustChangePassword && location.pathname !== '/password') {
    return <Navigate to="/password" replace />;
  }
  return <>{children}</>;
}

export function RequirePermission({
  permission,
  children,
  title = '没有访问权限',
}: {
  permission: Permission;
  children: ReactNode;
  title?: string;
}) {
  const can = useAuthStore((state) => state.can(permission));
  const session = useAuthStore((state) => state.session);

  if (!can) {
    return (
      <Card>
        <EmptyState
          icon="shield"
          title={title}
          description={`当前角色「${session?.role ?? ''}」不具备该权限。如果这是必要的，请联系组织管理员调整你的角色。`}
          action={
            <Button onClick={() => window.history.back()} variant="secondary">
              返回上一页
            </Button>
          }
        />
      </Card>
    );
  }
  return <>{children}</>;
}
